/**
 * The entry point Vite bundles. Providers, in the order they depend on each other,
 * and nothing else.
 *
 * `BrowserRouter` rather than a hash router because `src/server.ts` serves
 * `index.html` at every non-API path (the SPA fallback), so real URLs work on a cold
 * load and can be bookmarked, pasted into a ticket, and typed — which is what
 * `PRODUCT.md` §3's "an employee who navigates directly to `/productivity`" assumes.
 *
 * `StrictMode` is on. It double-invokes effects in development, which is the point:
 * the `/auth/me` effect in `SessionProvider` aborts its own request on cleanup, and
 * StrictMode is what proves that abort works before a slow network does.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { App } from './App';
import { SessionProvider } from './auth/SessionProvider';
import { I18nProvider } from './i18n';
import { ThemeProvider } from './shell/theme';
import './styles/app.css';

const container = document.getElementById('app-root');
if (container === null) {
  throw new Error('#app-root is missing from index.html; the SPA has nothing to mount into');
}

createRoot(container).render(
  <StrictMode>
    <I18nProvider>
      <ThemeProvider>
        <SessionProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </SessionProvider>
      </ThemeProvider>
    </I18nProvider>
  </StrictMode>,
);
